import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BookingForm } from '../components/BookingForm';
import { Phone, CheckCircle, ArrowRight, LockOpen, Clock, Shield, MapPin } from 'lucide-react';
import { businessInfo, services } from '../mock';

export const Lockouts = () => {
  const service = services.find(s => s.id === 'lockouts');
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/90 to-blue-950/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full backdrop-blur-sm mb-6">
              <LockOpen className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-semibold">Emergency Locksmith</span>
            </div>

            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6">
              Locked Out of Your Car?
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Don't panic! Our expert team provides fast, non-destructive vehicle entry services. We'll get you back in your car quickly and safely.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${businessInfo.phone}`}
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg shadow-blue-500/50"
              >
                <Phone className="w-5 h-5" />
                <span>Call {businessInfo.phone} Now</span>
              </a>
              <button
                onClick={() => setIsBookingOpen(true)}
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all border border-slate-700"
              >
                <span>Request Callback</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center space-x-4 p-6 bg-slate-800 rounded-xl">
              <div className="p-3 bg-blue-600 rounded-lg">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Fast Response</h3>
                <p className="text-gray-400">Typically 30-45 mins</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-6 bg-slate-800 rounded-xl">
              <div className="p-3 bg-blue-600 rounded-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">No Damage</h3>
                <p className="text-gray-400">Non-destructive entry</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-6 bg-slate-800 rounded-xl">
              <div className="p-3 bg-blue-600 rounded-lg">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Mobile Service</h3>
                <p className="text-gray-400">We come to you</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                What We Provide
              </h2>
              <div className="space-y-4">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-3 p-4 bg-slate-900 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">{feature}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-blue-900/30 border border-blue-700/50 rounded-xl">
                <h3 className="text-white font-bold mb-2">💡 Important Note</h3>
                <p className="text-blue-200 text-sm">
                  Please have your vehicle registration and proof of ownership ready when we arrive. This ensures we can help you quickly and securely.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-blue-600 rounded-2xl blur-3xl opacity-20"></div>
              <div className="relative bg-slate-900 p-8 rounded-2xl border border-slate-800">
                <h3 className="text-2xl font-bold text-white mb-6">Common Lockout Situations</h3>
                <ul className="space-y-4">
                  {[
                    "Keys locked inside the vehicle",
                    "Lost or misplaced car keys",
                    "Broken key in door lock",
                    "Faulty door lock mechanism",
                    "Boot/trunk lockout",
                    "Frozen or jammed locks"
                  ].map((situation, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-gray-300">{situation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-400">
              Getting you back in your vehicle in 3 simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Call Us",
                description: "Contact us immediately and provide your location and vehicle details"
              },
              {
                step: "2",
                title: "We Arrive",
                description: "Our expert technician arrives at your location within 30-45 minutes"
              },
              {
                step: "3",
                title: "Get Access",
                description: "We safely unlock your vehicle without causing any damage"
              }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="p-8 bg-slate-800 rounded-xl border border-slate-700 text-center h-full">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-950 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Immediate Help?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Call us now for fast, professional lockout assistance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${businessInfo.phone}`}
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg shadow-blue-500/50 text-lg"
            >
              <Phone className="w-6 h-6" />
              <span>Emergency Call: {businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Booking Modal */}
      <BookingForm
        service={service}
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
      />
    </div>
  );
};

export default Lockouts;
